/**
 * Ignition grid over a detection's area of uncertainty.
 *
 * A satellite detection is a pixel, not a point: the fire is somewhere within
 * that pixel's footprint (plus geolocation error), so igniting the exact
 * reported centre bakes in a false precision. This lays a regular lattice of
 * ignition points over the uncertainty area instead, which the simulator runs
 * as independent fires — the spread of outcomes then reflects where inside the
 * pixel the fire actually was.
 */

const M_PER_DEG_LAT = 111_320;

/** Half-width of a VIIRS pixel — the default uncertainty radius. */
export const DEFAULT_UNCERTAINTY_RADIUS_M = 190;
export const DEFAULT_GRID_SPACING_M = 100;
export const MIN_GRID_SPACING_M = 25;
/** Every point becomes an independent fire in the simulation, so keep it sane. */
export const MAX_GRID_POINTS = 121;

const lonScale = (lat: number) =>
  M_PER_DEG_LAT * Math.max(0.05, Math.cos((lat * Math.PI) / 180));

/**
 * Lattice of [lon, lat] points spaced *spacingM* apart, covering the disk of
 * radius *radiusM* around *centre*. Always includes the centre itself, and is
 * truncated at MAX_GRID_POINTS (nearest first, so the retained points stay
 * centred on the detection).
 */
export const uncertaintyGrid = (
  centre: [number, number],
  radiusM: number = DEFAULT_UNCERTAINTY_RADIUS_M,
  spacingM: number = DEFAULT_GRID_SPACING_M
): [number, number][] => {
  const [lon, lat] = centre;
  const spacing = Math.max(MIN_GRID_SPACING_M, spacingM);
  if (radiusM <= 0) return [[lon, lat]];

  const scaleLon = lonScale(lat);
  const steps = Math.floor(radiusM / spacing);

  const points: { coord: [number, number]; d: number }[] = [];
  for (let iy = -steps; iy <= steps; iy++) {
    for (let ix = -steps; ix <= steps; ix++) {
      const dx = ix * spacing;
      const dy = iy * spacing;
      const d = Math.hypot(dx, dy);
      if (d > radiusM) continue;
      points.push({
        coord: [lon + dx / scaleLon, lat + dy / M_PER_DEG_LAT],
        d,
      });
    }
  }
  if (!points.length) return [[lon, lat]];

  points.sort((a, b) => a.d - b.d);
  return points.slice(0, MAX_GRID_POINTS).map((p) => p.coord);
};

/** How many points the current settings would produce. */
export const uncertaintyGridCount = (
  radiusM: number,
  spacingM: number
): number => uncertaintyGrid([0, 0], radiusM, spacingM).length;
